import React, { useContext, useEffect, useState, useRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import { apiGet, apiPut, authFetch } from '../utils/api';
import './AdminUsers.css';
import { FaUserCircle, FaCamera, FaSave, FaKey, FaEye, FaEyeSlash } from 'react-icons/fa';

export default function Settings() {
  const { token, user, refreshUser } = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [toast, setToast] = useState(null);
  const [pwForm, setPwForm] = useState({ oldPassword: '', newPassword: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState(null);
  const fileInputRef = useRef();
  const [showPw, setShowPw] = useState({ old: false, new: false, confirm: false });
  const [pwStrength, setPwStrength] = useState('');

  useEffect(() => {
    async function fetchProfile() {
      setLoading(true);
      try {
        const res = await apiGet('/api/users/me', token);
        const data = await res.json();
        setProfile(data);
        setAvatarPreview(data.avatar ? data.avatar : null);
      } catch (e) {
        setToast({ type: 'error', msg: 'Failed to load profile.' });
      } finally {
        setLoading(false);
      }
    }
    if (token) fetchProfile();
  }, [token]);

  function handleChange(e) {
    setProfile(p => ({ ...p, [e.target.name]: e.target.value }));
  }

  function handleAvatarChange(e) {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  }

  async function handleAvatarUpload() {
    if (!avatarFile) return null;
    
    // First test if the upload route is accessible
    const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://intranet.nesimltd.co.ke';
    
    try {
      // Test the simple route first
      const testRes = await fetch(`${apiBaseUrl}/api/upload/simple`, { 
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!testRes.ok) {
        console.error('Test route failed:', testRes.status, testRes.statusText);
        throw new Error(`Test route failed: ${testRes.status}`);
      }
      
      const testData = await testRes.json();
      console.log('Test route response:', testData);
    } catch (testError) {
      console.error('Test route error:', testError);
      throw new Error(`Upload service not available: ${testError.message}`);
    }
    
    // Now try the actual upload
    const formData = new FormData();
    formData.append('attachment', avatarFile);
    
    const uploadUrl = `${apiBaseUrl}/api/upload`;
    
    const res = await fetch(uploadUrl, { 
      method: 'POST', 
      body: formData,
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.message || 'Upload failed');
    }
    const files = await res.json();
    return files[0]?.url || null;
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      let avatarUrl = profile.avatar;
      if (avatarFile) {
        avatarUrl = await handleAvatarUpload();
      }
      const body = {
        phone: profile.phone,
        address: profile.address,
        avatar: avatarUrl,
      };
      const res = await apiPut('/api/users/me', token, body);
      if (!res.ok) throw new Error('Failed to update profile');
      setToast({ type: 'success', msg: 'Profile updated!' });
      setAvatarFile(null);
      await refreshUser();
    } catch (e) {
      setToast({ type: 'error', msg: e.message || 'Update failed.' });
    } finally {
      setSaving(false);
    }
  }

  function checkStrength(pw) {
    if (!pw) return '';
    if (pw.length < 8) return 'Too short';
    let score = 0;
    if (/[a-z]/.test(pw)) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (score <= 1) return 'Weak';
    if (score === 2) return 'Fair';
    if (score === 3) return 'Good';
    if (score === 4) return 'Strong';
    return '';
  }

  async function handlePasswordChange(e) {
    e.preventDefault();
    setPwSaving(true);
    setPwError(null);
    if (pwForm.newPassword !== pwForm.confirm) {
      setPwError('Passwords do not match.');
      setPwSaving(false);
      return;
    }
    try {
      const res = await authFetch('/api/auth/change-password', token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          oldPassword: pwForm.oldPassword,
          newPassword: pwForm.newPassword,
        }),
      });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(err || 'Failed to change password');
      }
      setToast({ type: 'success', msg: 'Password updated!' });
      setPwForm({ oldPassword: '', newPassword: '', confirm: '' });
    } catch (e) {
      setPwError(e.message);
    } finally {
      setPwSaving(false);
    }
  }

  return (
    <div className="admin-users-outer settings-container" style={{ 
      maxWidth: 1400, 
      margin: '50px auto', 
      background: '#ffffff', 
      borderRadius: 24, 
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1), 0 8px 16px rgba(0, 0, 0, 0.06)', 
      padding: 56,
      border: '1px solid #e2e8f0',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Decorative background elements */}
      <div style={{
        position: 'absolute',
        top: -50,
        right: -50,
        width: 200,
        height: 200,
        background: '#3b82f6',
        borderRadius: '50%',
        opacity: 0.05,
        filter: 'blur(40px)'
      }} />
      <div style={{
        position: 'absolute',
        bottom: -30,
        left: -30,
        width: 150,
        height: 150,
        background: '#1d4ed8',
        borderRadius: '50%',
        opacity: 0.04,
        filter: 'blur(30px)'
      }} />
      
      <h2 style={{ 
        fontWeight: 800, 
        fontSize: 36, 
        marginBottom: 48,
        color: '#1e40af',
        textAlign: 'center',
        letterSpacing: '-0.5px'
      }}>Settings</h2>
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', fontSize: '18px', color: '#64748b' }}>
          <div style={{ fontSize: '24px', marginBottom: '16px', color: '#3b82f6' }}>Loading...</div>
          Loading your profile...
        </div>
      ) : profile && (
        <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 40 }}>
          {/* Profile Header Section */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 48,
            padding: '32px',
            background: '#f8fafc',
            borderRadius: 20,
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{ position: 'relative' }}>
              {avatarPreview ? (
                <img src={avatarPreview} alt="avatar" style={{ 
                  width: 140, 
                  height: 140, 
                  borderRadius: '50%', 
                  objectFit: 'cover', 
                  border: '4px solid #ffffff',
                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
                }} />
              ) : (
                <FaUserCircle style={{ 
                  width: 140, 
                  height: 140, 
                  color: '#3b82f6',
                  filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.1))'
                }} />
              )}
              <button 
                type="button" 
                onClick={() => fileInputRef.current.click()} 
                style={{ 
                  position: 'absolute', 
                  bottom: 8, 
                  right: 8, 
                  background: '#3b82f6', 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: '50%', 
                  width: 44, 
                  height: 44, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)', 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontSize: '18px'
                }} 
                title="Change profile picture"
                onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
              >
                <FaCamera />
              </button>
              <input type="file" accept="image/*" ref={fileInputRef} style={{ display: 'none' }} onChange={handleAvatarChange} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ 
                fontWeight: 700, 
                fontSize: 32, 
                marginBottom: 16,
                color: '#1e293b',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
              }}>{profile.name}</div>
              <div style={{ 
                color: '#64748b', 
                fontSize: 20, 
                marginBottom: 8,
                fontWeight: 500
              }}>{profile.email}</div>
              <div style={{ 
                color: '#3b82f6', 
                fontSize: 18,
                fontWeight: 600,
                padding: '8px 16px',
                background: 'rgba(59, 130, 246, 0.1)',
                borderRadius: '20px',
                display: 'inline-block',
                border: '1px solid rgba(59, 130, 246, 0.2)'
              }}>Access: <b>{profile.role}</b></div>
            </div>
          </div>
          
          {/* Profile Form Fields */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr 1fr', 
            gap: 32,
            padding: '32px',
            background: 'rgba(255, 255, 255, 0.7)',
            borderRadius: 20,
            border: '1px solid rgba(226, 232, 240, 0.6)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)'
          }}>
            <label style={{ fontWeight: 600, fontSize: '16px' }}>Name
              <input 
                name="name" 
                value={profile.name || ''} 
                className="admin-users-input settings-input" 
                style={{ 
                  width: '100%',
                  marginTop: '8px',
                  padding: '14px 16px',
                  borderRadius: '12px',
                  border: '2px solid #e2e8f0',
                  fontSize: '16px',
                  transition: 'all 0.3s ease',
                  background: '#ffffff'
                }} 
                readOnly 
              />
            </label>
            <label style={{ fontWeight: 600, fontSize: '16px' }}>Email
              <input 
                name="email" 
                value={profile.email || ''} 
                className="admin-users-input settings-input" 
                style={{ 
                  width: '100%',
                  marginTop: '8px',
                  padding: '14px 16px',
                  borderRadius: '12px',
                  border: '2px solid #e2e8f0',
                  fontSize: '16px',
                  transition: 'all 0.3s ease',
                  background: '#ffffff'
                }} 
                readOnly 
              />
            </label>
            <label style={{ fontWeight: 600, fontSize: '16px' }}>Phone
              <input 
                name="phone" 
                value={profile.phone || ''} 
                onChange={handleChange} 
                className="admin-users-input settings-input" 
                style={{ 
                  width: '100%',
                  marginTop: '8px',
                  padding: '14px 16px',
                  borderRadius: '12px',
                  border: '2px solid #e2e8f0',
                  fontSize: '16px',
                  transition: 'all 0.3s ease',
                  background: '#ffffff'
                }} 
              />
            </label>
            <label style={{ fontWeight: 600, fontSize: '16px' }}>Address
              <input 
                name="address" 
                value={profile.address || ''} 
                onChange={handleChange} 
                className="admin-users-input settings-input" 
                style={{ 
                  width: '100%',
                  marginTop: '8px',
                  padding: '14px 16px',
                  borderRadius: '12px',
                  border: '2px solid #e2e8f0',
                  fontSize: '16px',
                  transition: 'all 0.3s ease',
                  background: '#ffffff'
                }} 
              />
            </label>
          </div>
          
          <button 
            type="submit" 
            className="create-btn prominent settings-save-btn" 
            style={{ 
              alignSelf: 'center', 
              minWidth: 180, 
              padding: '16px 32px', 
              fontSize: '18px',
              fontWeight: '600',
              background: '#3b82f6',
              border: 'none',
              borderRadius: '16px',
              color: 'white',
              boxShadow: '0 8px 25px rgba(59, 130, 246, 0.4)',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }} 
            disabled={saving}
            onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
          >
            <FaSave style={{ marginRight: 12 }} /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      )}
      
      <hr style={{ 
        margin: '56px 0', 
        border: 0, 
        borderTop: '2px solid rgba(226, 232, 240, 0.8)',
        borderRadius: '1px'
      }} />
      
      {/* Password Change Section */}
      <div style={{
        padding: '32px',
        background: '#f8fafc',
        borderRadius: 20,
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
      }}>
        <h3 style={{ 
          fontWeight: 700, 
          fontSize: 26, 
          marginBottom: 28,
          color: '#1e293b',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span style={{
            padding: '8px 12px',
            background: '#3b82f6',
            borderRadius: '12px',
            color: 'white',
            fontSize: '18px'
          }}>
            <FaKey />
          </span>
          Change Password
        </h3>
        
        <form onSubmit={handlePasswordChange} style={{ display: 'flex', flexDirection: 'column', gap: 28, maxWidth: 700 }}>
          <label style={{ fontWeight: 600, fontSize: '16px' }}>Current Password
            <div style={{ position: 'relative', marginTop: '8px' }}>
              <input 
                type={showPw.old ? 'text' : 'password'} 
                value={pwForm.oldPassword} 
                onChange={e => setPwForm(f => ({ ...f, oldPassword: e.target.value }))} 
                className="admin-users-input settings-input" 
                required 
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  borderRadius: '12px',
                  border: '2px solid #e2e8f0',
                  fontSize: '16px',
                  transition: 'all 0.3s ease',
                  background: '#ffffff'
                }}
              />
              <span 
                onClick={() => setShowPw(s => ({ ...s, old: !s.old }))} 
                style={{ 
                  position: 'absolute', 
                  right: 16, 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  cursor: 'pointer', 
                  color: '#64748b',
                  fontSize: '18px',
                  transition: 'color 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = '#3b82f6'}
                onMouseLeave={(e) => e.target.style.color = '#64748b'}
              >
                {showPw.old ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
          </label>
          
          <label style={{ fontWeight: 600, fontSize: '16px' }}>New Password
            <div style={{ position: 'relative', marginTop: '8px' }}>
              <input 
                type={showPw.new ? 'text' : 'password'} 
                value={pwForm.newPassword} 
                onChange={e => { setPwForm(f => ({ ...f, newPassword: e.target.value })); setPwStrength(checkStrength(e.target.value)); }} 
                className="admin-users-input settings-input" 
                required 
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  borderRadius: '12px',
                  border: '2px solid #e2e8f0',
                  fontSize: '16px',
                  transition: 'all 0.3s ease',
                  background: '#ffffff'
                }}
              />
              <span 
                onClick={() => setShowPw(s => ({ ...s, new: !s.new }))} 
                style={{ 
                  position: 'absolute', 
                  right: 16, 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  cursor: 'pointer', 
                  color: '#64748b',
                  fontSize: '18px',
                  transition: 'color 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = '#3b82f6'}
                onMouseLeave={(e) => e.target.style.color = '#64748b'}
              >
                {showPw.new ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
            {pwForm.newPassword && (
              <div style={{ 
                fontSize: 14, 
                marginTop: 8, 
                color: pwStrength === 'Strong' ? '#22c55e' : pwStrength === 'Good' ? '#eab308' : '#ef4444', 
                fontWeight: 600,
                padding: '6px 12px',
                background: pwStrength === 'Strong' ? 'rgba(34, 197, 94, 0.1)' : pwStrength === 'Good' ? 'rgba(234, 179, 8, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                borderRadius: '8px',
                display: 'inline-block',
                border: `1px solid ${pwStrength === 'Strong' ? 'rgba(34, 197, 94, 0.3)' : pwStrength === 'Good' ? 'rgba(234, 179, 8, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
              }}>
                Strength: {pwStrength}
              </div>
            )}
          </label>
          
          <label style={{ fontWeight: 600, fontSize: '16px' }}>Confirm New Password
            <div style={{ position: 'relative', marginTop: '8px' }}>
              <input 
                type={showPw.confirm ? 'text' : 'password'} 
                value={pwForm.confirm} 
                onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} 
                className="admin-users-input settings-input" 
                required 
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  borderRadius: '12px',
                  border: '2px solid #e2e8f0',
                  fontSize: '16px',
                  transition: 'all 0.3s ease',
                  background: '#ffffff'
                }}
              />
              <span 
                onClick={() => setShowPw(s => ({ ...s, confirm: !s.confirm }))} 
                style={{ 
                  position: 'absolute', 
                  right: 16, 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  cursor: 'pointer', 
                  color: '#64748b',
                  fontSize: '18px',
                  transition: 'color 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = '#3b82f6'}
                onMouseLeave={(e) => e.target.style.color = '#64748b'}
              >
                {showPw.confirm ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
          </label>
          
          {pwError && (
            <div style={{ 
              color: '#ef4444', 
              fontWeight: 600,
              padding: '12px 16px',
              background: 'rgba(239, 68, 68, 0.1)',
              borderRadius: '12px',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              {pwError}
            </div>
          )}
          
          <button 
            type="submit" 
            className="create-btn settings-password-btn" 
            style={{ 
              alignSelf: 'center', 
              minWidth: 180, 
              padding: '16px 32px', 
              fontSize: '18px',
              fontWeight: '600',
              background: '#3b82f6',
              border: 'none',
              borderRadius: '16px',
              color: 'white',
              boxShadow: '0 8px 25px rgba(59, 130, 246, 0.4)',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }} 
            disabled={pwSaving}
            onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
          >
            {pwSaving ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </div>
      
      {toast && (
        <div className="toast settings-toast" style={{ 
          marginTop: 32, 
          background: toast.type === 'success' ? '#dcfce7' : '#fef2f2', 
          color: toast.type === 'success' ? '#166534' : '#dc2626', 
          border: `2px solid ${toast.type === 'success' ? '#22c55e' : '#ef4444'}`, 
          borderRadius: 16, 
          padding: 20, 
          fontWeight: 600, 
          textAlign: 'center',
          boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
          fontSize: '16px'
        }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
} 